const owns = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const isRecord = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const questionCode = (question) =>
  typeof question?.code === "string" && question.code ? question.code : null;
const questionTitleIdentity = (question) =>
  typeof question?.title === "string" &&
  question.title &&
  typeof question?.type === "string" &&
  question.type
    ? JSON.stringify([question.type, question.title])
    : null;

function indexUniqueIdentity(index, identity, destination) {
  if (identity === null) return;
  index.set(identity, index.has(identity) ? null : destination);
}

function withoutStrayMedicationState(answer, question) {
  if (
    question.type === "medication-review" ||
    (!owns(answer, "medicationReview") && !owns(answer, "reloadRequired"))
  ) {
    return answer;
  }
  const next = { ...answer };
  delete next.medicationReview;
  delete next.reloadRequired;
  // Checkbox/radio answers use their choices. This top-level value came from
  // the medication answer that previously occupied their numeric slot.
  if (
    owns(answer, "medicationReview") &&
    ["checkbox", "radio"].includes(question.type)
  ) {
    delete next.value;
  }
  return next;
}

// A draft's numeric slots can outlive a question move. Recover answers by their
// recorded question identity without assigning them to a different question.
export function reconcileIntakeAnswers(questionnaire, answers) {
  const questions = questionnaire?.questions || [];
  const codes = new Map();
  const titles = new Map();
  questions.forEach((question, destination) => {
    const code = questionCode(question);
    if (code !== null) indexUniqueIdentity(codes, code, destination);
    else
      indexUniqueIdentity(titles, questionTitleIdentity(question), destination);
  });

  const candidates = [];
  for (const [key, answer] of Object.entries(answers || {})) {
    if (!/^(0|[1-9]\d*)$/.test(key) || !isRecord(answer)) continue;
    const source = Number(key);
    if (!Number.isSafeInteger(source)) continue;
    let destination;
    if (answer.question == null) {
      // Older ordinary drafts sometimes omit question metadata. Medication
      // drafts require an identity so they cannot attach to an unrelated slot.
      if (source >= questions.length || owns(answer, "medicationReview"))
        continue;
      destination = source;
    } else {
      if (!isRecord(answer.question)) continue;
      const code = questionCode(answer.question);
      destination =
        code !== null
          ? codes.get(code)
          : titles.get(questionTitleIdentity(answer.question));
      if (destination == null) continue;
    }
    candidates.push({ source, destination, answer });
  }

  const reconciled = {};
  for (const { source, destination, answer } of candidates) {
    if (source === destination) {
      reconciled[destination] = withoutStrayMedicationState(
        answer,
        questions[destination],
      );
    }
  }

  const displaced = new Map();
  for (const candidate of candidates) {
    if (owns(reconciled, candidate.destination)) continue;
    const matches = displaced.get(candidate.destination) || [];
    matches.push(candidate);
    displaced.set(candidate.destination, matches);
  }
  for (const [destination, matches] of displaced) {
    // Without an answer already in its current slot, conflicting drafts have
    // no reliable recency order. Leave the question for the patient to answer.
    if (matches.length !== 1) continue;
    reconciled[destination] = withoutStrayMedicationState(
      matches[0].answer,
      questions[destination],
    );
  }
  return reconciled;
}
